import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Paper, Button, Divider, Chip, List, ListItem, ListItemText, Stack, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import apiService from '../services/api.service';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const Reports: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  // Log Reports state
  const [store, setStore] = useState<'db' | 'file'>('db');
  const [logReportType, setLogReportType] = useState<'application' | 'subcontracting'>('application');
  const [source, setSource] = useState<'winston' | 'pino'>('winston');
  const [level, setLevel] = useState<string>('');
  const [logDate, setLogDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [search, setSearch] = useState<string>('');
  const [limit, setLimit] = useState<number>(200);
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [subcontractTransactionType, setSubcontractTransactionType] = useState<string>('');
  const [subcontractStatus, setSubcontractStatus] = useState<string>('');
  const [copiedKey, setCopiedKey] = useState<string>('');
  const logRequestSequence = useRef(0);

  // Approval Tracking state
  const [filterMode, setFilterMode] = useState<'single' | 'range'>('single');
  const [singleDate, setSingleDate] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [prNumber, setPrNumber] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loadingTracking, setLoadingTracking] = useState<boolean>(false);
  const [trackingData, setTrackingData] = useState<any[]>([]);
  const [exportingTracking, setExportingTracking] = useState<boolean>(false);

  

  const levelOptions = useMemo(() => (
    source === 'pino'
      ? ['trace','debug','info','warn','error','fatal']
      : ['','info','warn','error','debug']
  ), [source]);
  const visibleLogs = useMemo(() => (
    logReportType === 'subcontracting'
      ? logs.filter((row) => String(row.source || row.Source || '').toLowerCase() === 'subcontracting')
      : logs
  ), [logReportType, logs]);

  const fetchLogs = async () => {
    const requestSequence = ++logRequestSequence.current;
    try {
      setLoadingLogs(true);
      const res = await apiService.getLogReports({
        reportType: logReportType,
        source,
        date: logDate,
        level: level || undefined,
        transactionType: logReportType === 'subcontracting' ? subcontractTransactionType || undefined : undefined,
        status: logReportType === 'subcontracting' ? subcontractStatus || undefined : undefined,
        limit,
        search: search || undefined,
        store,
      });
      const data = (res as any)?.data || (res as any);
      if (requestSequence === logRequestSequence.current) setLogs(data?.logs || []);
    } catch (e: any) {
      if (requestSequence === logRequestSequence.current) setLogs([]);
    } finally {
      if (requestSequence === logRequestSequence.current) setLoadingLogs(false);
    }
  };

  const copyAuditValue = async (value: any, key: string) => {
    await navigator.clipboard.writeText(JSON.stringify(value ?? null, null, 2));
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey((current) => current === key ? '' : current), 1500);
  };

  const fetchApprovalTracking = async () => {
    try {
      setLoadingTracking(true);
      const params: any = {};

      if (filterMode === 'single' && singleDate) {
        params.date = singleDate;
      } else if (filterMode === 'range') {
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }

      if (prNumber) params.prNumber = prNumber;
      if (statusFilter) params.status = statusFilter;

      const res = await apiService.getApprovalTracking(params);
      const data = (res as any)?.data || (res as any);
      setTrackingData(data?.records || []);
    } catch (e: any) {
      setTrackingData([]);
      console.error('Error fetching approval tracking:', e);
    } finally {
      setLoadingTracking(false);
    }
  };

  const handleExportTracking = async () => {
    try {
      setExportingTracking(true);
      const params: any = {};

      if (filterMode === 'single' && singleDate) {
        params.date = singleDate;
      } else if (filterMode === 'range') {
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }

      if (prNumber) params.prNumber = prNumber;
      if (statusFilter) params.status = statusFilter;

      await apiService.exportApprovalTracking(params);
    } catch (e: any) {
      console.error('Error exporting approval tracking:', e);
    } finally {
      setExportingTracking(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, level, logDate, limit, logReportType, subcontractTransactionType, subcontractStatus, tabValue]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchApprovalTracking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  return (
    <Paper component="section" aria-labelledby="reports-heading" elevation={0} sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Typography
        component="h1"
        id="reports-heading"
        variant="h6"
        gutterBottom
        sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700 }}
      >
        Reports
      </Typography>

      <Tabs
        value={tabValue}
        onChange={(_, newValue) => setTabValue(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab label="Log Reports" />
        <Tab label="Approval Flow Tracking" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <select value={logReportType} onChange={(e) => {
            const nextType = e.target.value as 'application'|'subcontracting';
            setLogReportType(nextType);
            if (nextType === 'subcontracting') setStore('db');
          }} style={{ height: 30 }}>
            <option value="application">Application</option>
            <option value="subcontracting">Subcontracting</option>
          </select>
          <select value={store} disabled={logReportType === 'subcontracting'} onChange={(e) => setStore((e.target.value as 'db'|'file'))} style={{ height: 30 }}>
            <option value="db">Database</option>
            <option value="file">File</option>
          </select>
          {store === 'file' && (
            <select value={source} onChange={(e) => setSource((e.target.value as 'winston'|'pino'))} style={{ height: 30 }}>
              <option value="winston">Winston</option>
              <option value="pino">Pino</option>
            </select>
          )}
          {logReportType === 'subcontracting' && (
            <>
              <select value={subcontractTransactionType} onChange={(e) => setSubcontractTransactionType(e.target.value)} style={{ height: 30 }}>
                <option value="">All Transactions</option>
                <option value="Subcontract Creation">Subcontract Creation</option>
                <option value="Subcontract Update">Subcontract Update</option>
                <option value="Subcontract Purchase Order">Subcontract Purchase Order</option>
                <option value="Work Done GRPO">Work Done GRPO</option>
                <option value="Advance AP Down Payment">Advance AP Down Payment</option>
                <option value="Interim AP Invoice">Interim AP Invoice</option>
                <option value="Goods Issue">Goods Issue</option>
                <option value="Deduction Release AP Invoice">Deduction Release AP Invoice</option>
              </select>
              <select value={subcontractStatus} onChange={(e) => setSubcontractStatus(e.target.value)} style={{ height: 30 }}>
                <option value="">All Statuses</option>
                <option value="Success">Success</option>
                <option value="Failed">Failed</option>
              </select>
            </>
          )}
          <select value={level} onChange={(e) => setLevel(e.target.value)} style={{ height: 30 }}>
            {levelOptions.map((lv) => (
              <option key={lv || 'all'} value={lv}>{lv || 'All'}</option>
            ))}
          </select>
          <input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} style={{ height: 28 }} />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={logReportType === 'subcontracting' ? 'Contract, PC, DocNum, endpoint...' : 'Search...'} style={{ height: 28, minWidth: 220 }} />
          <input type="number" value={String(limit)} onChange={(e) => setLimit(Math.max(10, Math.min(1000, Number(e.target.value || 200))))} style={{ height: 28, width: 100 }} />
          <Button variant="contained" size="small" onClick={fetchLogs} disabled={loadingLogs}>Refresh</Button>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Chip label={`Count: ${visibleLogs.length}`} size="small" />
            <Chip label={`Report: ${logReportType === 'subcontracting' ? 'Subcontracting' : 'Application'}`} size="small" />
            <Chip label={`Store: ${store}`} size="small" />
            {store === 'file' && <Chip label={`Source: ${source}`} size="small" />}
            {level && <Chip label={`Level: ${level}`} size="small" />}
          </Stack>
          <List dense sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {visibleLogs.map((row, idx) => {
              const ts = row.timestamp || row.time || row.ts || row['@timestamp'] || '';
              const lv = row.level && typeof row.level === 'number' ? ({ 10:'trace',20:'debug',30:'info',40:'warn',50:'error',60:'fatal' } as any)[row.level] : (row.level || '');
              const msg = row.message || row.msg || '';
              const primary = `${ts ? ts : ''} ${lv ? '[' + lv + ']' : ''} ${String(msg).slice(0, 200)}`.trim();
              const secondaryObj = { ...row };
              delete secondaryObj.message; delete secondaryObj.msg; delete secondaryObj.level; delete secondaryObj.timestamp; delete secondaryObj.time;
              const auditMeta = row.meta || row.Meta || {};
              const auditSummary = [
                auditMeta.endpoint,
                auditMeta.subcontractNumber,
                auditMeta.paymentNumber,
                auditMeta.sapDocNum ? `SAP DocNum ${auditMeta.sapDocNum}` : '',
                auditMeta.status,
              ].filter(Boolean).join(' | ');
              return (
                <ListItem key={row.id || idx} disableGutters sx={{ py: 0.5, display: 'block', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <ListItemText
                    primaryTypographyProps={{ variant: 'body2', fontWeight: logReportType === 'subcontracting' ? 600 : 400 }}
                    primary={primary}
                    secondary={logReportType === 'subcontracting' ? auditSummary : (Object.keys(secondaryObj).length ? JSON.stringify(secondaryObj) : undefined)}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  {logReportType === 'subcontracting' && (
                    <Box component="details" sx={{ ml: 1, '& summary': { cursor: 'pointer', fontSize: 12, color: 'primary.main' } }}>
                      <Box component="summary">View SAP request, response and error</Box>
                      <Stack direction="row" spacing={1} sx={{ my: 0.75 }}>
                        <Button size="small" variant="outlined" onClick={() => copyAuditValue(auditMeta.requestPayload, `${row.id}-request`)}>
                          {copiedKey === `${row.id}-request` ? 'Copied' : 'Copy Request'}
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => copyAuditValue(auditMeta.responsePayload, `${row.id}-response`)}>
                          {copiedKey === `${row.id}-response` ? 'Copied' : 'Copy Response'}
                        </Button>
                        {auditMeta.error && (
                          <Button size="small" color="error" variant="outlined" onClick={() => copyAuditValue(auditMeta.error, `${row.id}-error`)}>
                            {copiedKey === `${row.id}-error` ? 'Copied' : 'Copy Error'}
                          </Button>
                        )}
                      </Stack>
                      <Typography variant="caption" fontWeight={700}>SAP Request Payload</Typography>
                      <Box
                        component="pre"
                        sx={{ m: 0.5, p: 1, maxHeight: 260, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {JSON.stringify(auditMeta.requestPayload ?? null, null, 2)}
                      </Box>
                      <Typography variant="caption" fontWeight={700}>SAP Response</Typography>
                      <Box component="pre" sx={{ m: 0.5, p: 1, maxHeight: 260, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(auditMeta.responsePayload ?? auditMeta.error ?? null, null, 2)}
                      </Box>
                    </Box>
                  )}
                </ListItem>
              );
            })}
            {visibleLogs.length === 0 && (
              <ListItem disableGutters sx={{ py: 0.5 }}>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body2' }}
                  primary={loadingLogs ? 'Loading…' : (logReportType === 'subcontracting'
                    ? 'No subcontract SAP payloads found for this date. Payload logging starts with transactions processed after this feature is deployed.'
                    : 'No logs found')}
                />
              </ListItem>
            )}
          </List>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Filter</InputLabel>
            <Select value={filterMode} onChange={(e) => setFilterMode(e.target.value as 'single' | 'range')} label="Date Filter">
              <MenuItem value="single">Single Date</MenuItem>
              <MenuItem value="range">Date Range</MenuItem>
            </Select>
          </FormControl>

          {filterMode === 'single' ? (
            <TextField
              type="date"
              size="small"
              label="Date"
              value={singleDate}
              onChange={(e) => setSingleDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <>
              <TextField
                type="date"
                size="small"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                size="small"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          <TextField
            size="small"
            label="PR Number"
            value={prNumber}
            onChange={(e) => setPrNumber(e.target.value)}
            placeholder="Search PR..."
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
              <MenuItem value="Reverted Back">Reverted Back</MenuItem>
              <MenuItem value="SAP Posted">SAP Posted</MenuItem>
            </Select>
          </FormControl>

          <Button variant="contained" size="small" onClick={fetchApprovalTracking} disabled={loadingTracking}>
            Search
          </Button>
          <Button variant="outlined" size="small" onClick={handleExportTracking} disabled={exportingTracking || trackingData.length === 0}>
            Export Excel
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Chip label={`Records: ${trackingData.length}`} size="small" />
          </Stack>

          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>PR Number</TableCell>
                  <TableCell>PR Date</TableCell>
                  <TableCell>Requester</TableCell>
                  <TableCell>Step</TableCell>
                  <TableCell>Approver Role</TableCell>
                  <TableCell>Approver Name</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Action Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Comments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trackingData.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{row.prnumber}</TableCell>
                    <TableCell>{row.prdate ? new Date(row.prdate).toLocaleDateString() : ''}</TableCell>
                    <TableCell>{row.requestername}</TableCell>
                    <TableCell>{row.stepnumber}</TableCell>
                    <TableCell>{row.approverrole}</TableCell>
                    <TableCell>{row.approvername}</TableCell>
                    <TableCell>{row.action}</TableCell>
                    <TableCell>{row.actiondate ? new Date(row.actiondate).toLocaleString() : ''}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        color={
                          row.status === 'Approved' ? 'success' :
                          row.status === 'Rejected' ? 'error' :
                          row.status === 'Pending' ? 'warning' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{row.comments}</TableCell>
                  </TableRow>
                ))}
                {trackingData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      {loadingTracking ? 'Loading...' : 'No records found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </TabPanel>
    </Paper>
  );
};

export default Reports;
